// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Skeleton from "@vanmeter/App/Components/Skeleton/Skeleton";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { getImageSize, parseBoolean } from "@vanmeter/XDL/Utilities";
import React, { useEffect, useState } from "react";
import ReactImageMagnify from "react-image-magnify";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    image: ImageModel;
    useLargeImage?: boolean;
    onClick?: () => void;
    extendedStyles?: ProductPrimaryImageStyles;
    // XNG-Changes
    useMagnify?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

// XNG-Change: Add not found image & isDesktop.
const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const { productSettings, catalogSettings: { notFoundLargeImagePath, notFoundMediumImagePath } = {} as any } =
        getSettingsCollection(state);

    const notFoundImage = !props.useLargeImage
        ? notFoundMediumImagePath
        : notFoundLargeImagePath || notFoundMediumImagePath;
    return {
        productSettings,
        // XNG-Change
        notFoundImage,
        isDesktop: !(state as CustomAppState).xCommon.isTabletScreen,
    };
};

export interface ProductPrimaryImageStyles {
    centeringWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    hiddenWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    spinner?: LoadingSpinnerProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    // XNG-Changes
    skeletonStyle?: InjectableCss;
}

export const productPrimaryImageStyles: ProductPrimaryImageStyles = {
    centeringWrapper: {
        css: css`
            min-height: 300px;
            margin: 10px 0;
            display: flex;
            align-items: center;
            justify-content: center;
            img {
                height: auto;
                max-width: 100%;
            }
        `,
    },
    image: {
        css: css`
            margin: auto;
            cursor: pointer;
        `,
    },
    img: {
        css: css`
            cursor: pointer;
        `,
    },
};

const ProductPrimaryImage = ({
    productSettings,
    product,
    image,
    useLargeImage,
    onClick,
    extendedStyles,
    // XNG-Change
    notFoundImage,
    useMagnify,
    isDesktop,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productPrimaryImageStyles, extendedStyles));
    // <xng-change>
    const [imageSrc, setImageSrc] = useState<ImageModel>(image);
    const [isImageLoading, setIsImageLoading] = useState(true);

    const [largeImageDimensions, setLargeImageDimensions] = useState({
        width: 0,
        height: 0,
    });

    const path = useLargeImage
        ? image.largeImagePath || image.mediumImagePath
        : image.mediumImagePath || image.largeImagePath;

    const [enableZoom, setEnableZoom] = useState(!parseBoolean(product.properties.isNotFoundMediumImage));

    // </xng-change>
    useEffect(() => {
        (async () => {
            if (!image.largeImagePath) {
                setEnableZoom(false);
                return;
            }
            const result = await getImageSize(image.largeImagePath);
            setLargeImageDimensions(result);
        })();
    }, [image.largeImagePath]);

    useEffect(() => {
        if ((window as any).sirvScriptAdded) {
            return;
        }

        if (
            productSettings.imageProvider !== "SIRV" ||
            !product.images ||
            product.images.every(o => o.imageType !== "360")
        ) {
            return;
        }

        const script = document.createElement("script");
        script.src = "https://scripts.sirv.com/sirv.js";
        script.async = true;

        document.body.appendChild(script);
        (window as any).sirvScriptAdded = true;
    }, [productSettings.imageProvider, product]);

    // <xng-change>
    useEffect(() => {
        setIsImageLoading(true);
    }, [path]);

    useEffect(() => {
        setImageSrc(image);
    }, [image]);

    const handleNotFoundImage = (isSmallImage?: boolean) => {
        const imagePathKey = isSmallImage ? "mediumImagePath" : "largeImagePath";

        setImageSrc(prevState => ({
            ...prevState,
            [imagePathKey]: notFoundImage || "",
        }));
        setEnableZoom(false);
    };
    // </xng-change>

    if (!product) {
        return null;
    }

    // XNG-Change: Enable fallback image if any.
    if (!path && !notFoundImage) {
        return null;
    }

    const imageWrapperClickHandler = () => {
        onClick?.();
    };

    const imageWrapperKeyHandler: React.KeyboardEventHandler<HTMLImageElement> = event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Enter") {
            onClick?.();
        }
    };
    // XNG-Changes
    const handleImageLoad = () => {
        setIsImageLoading(false);
    };

    const canMagnify = useMagnify && isDesktop && enableZoom && !isImageLoading && largeImageDimensions.width > 0;

    if (canMagnify) {
        return (
            <>
                <link
                    rel="preload"
                    as="image"
                    href={imageSrc.mediumImagePath || imageSrc.largeImagePath}
                    {...{ fetchPriority: "high" }}
                />
                <ReactImageMagnify
                    {...{
                        smallImage: {
                            alt: imageSrc.imageAltText,
                            src: imageSrc.mediumImagePath,
                            isFluidWidth: true,
                            onError() {
                                handleNotFoundImage(true);
                            },
                        },
                        largeImage: {
                            src: imageSrc.largeImagePath,
                            width: largeImageDimensions.width,
                            height: largeImageDimensions.height,
                            onError() {
                                handleNotFoundImage();
                            },
                        },
                        enlargedImageContainerClassName: "enlarged-img",
                        enlargedImagePosition: "beside",
                        imageClassName: "medium-image",
                        className: "medium-image-container",
                        style: {
                            cursor: "zoom-in",
                        },
                    }}
                />
            </>
        );
    }
    // </XNG-Changes>

    return (
        <>
            {image.imageType === "Static" && (
                <StyledWrapper {...styles.centeringWrapper} onClick={imageWrapperClickHandler}>
                    {/* XNG-Changes */}
                    {isImageLoading && <Skeleton width="430px" height="430px" {...styles.skeletonStyle} />}
                    <Img
                        src={path}
                        altText={image.imageAltText}
                        loading="eager"
                        data-test-selector="productDetails_mainImage"
                        onKeyPress={imageWrapperKeyHandler}
                        tabIndex={0}
                        // XNG-Changes
                        onLoad={handleImageLoad}
                        style={{
                            display: isImageLoading ? "none" : undefined,
                            cursor: enableZoom ? "pointer" : "auto",
                        }}
                        // {...styles.img}
                    />
                </StyledWrapper>
            )}
            {image.imageType === "360" && productSettings.imageProvider === "SIRV" && (
                <div style={{ minHeight: 300 }} onClick={imageWrapperClickHandler}>
                    <div className="Sirv" key={image.id} data-src={image.mediumImagePath} />
                </div>
            )}
        </>
    );
};

export default connect(mapStateToProps)(ProductPrimaryImage);
